import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  IconButton,
  useToast,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  SimpleGrid,
  Image,
  Flex,
  Progress,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Wrap,
  WrapItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverCloseButton,
  CheckboxGroup,
  Textarea,
  Link
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, AddIcon, ViewIcon, WarningIcon } from '@chakra-ui/icons';
import { FaCloudUploadAlt, FaImage, FaVideo, FaFolder, FaUsers, FaTags, FaHome, FaFilePdf, FaSyncAlt, FaEdit } from 'react-icons/fa';
import { useRecoilValue } from 'recoil';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { userAtom } from '../state/userAtom';
import {
  getMedia,
  getTopics,
  uploadMedia,
  updateMedia,
  deleteMedia,
  createTopic,
  updateTopic,
  deleteTopic,
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  backfillDimensions,
  revertSiteContent,
  getSiteContent,
  updateSiteContent,
  getUnregisteredFiles,
  registerMedia,
  deleteUnregisteredFile
} from '../api/media';

const MEDIA_TYPE_OPTIONS = [
  { value: 'video', label: 'Video' },
  { value: 'social', label: 'Social' },
  { value: 'print', label: 'Print' },
  { value: 'digital', label: 'Digital' }
];

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: FaHome, roles: ['admin', 'editor'] },
  { id: 'upload', label: 'Upload Media', icon: FaCloudUploadAlt, roles: ['admin', 'editor'] },
  { id: 'media', label: 'Media Library', icon: FaImage, roles: ['admin', 'editor'] },
  { id: 'homepage', label: 'Website Text', icon: FaEdit, roles: ['admin'] },
  { id: 'topics', label: 'Topics', icon: FaTags, roles: ['admin'] },
  { id: 'users', label: 'Users', icon: FaUsers, roles: ['admin'] }
];

const DEFAULT_CONTENT = {
  hero_heading: 'Here are the tools to tell our story.',
  intro_text: "We have a great story. We do essential things for humankind.\nWe are one of the most technologically advanced industries on the planet.\nModern life doesn't happen without us. So, let's get our story out.",
  toolbox_heading: "Here's how to use our messaging toolbox.",
  toolbox_text: "We've included a variety of video, print, digital and social assets intended for broad use by IADC Members and the drilling industry. Assets can be filtered by topic and/or media type and downloaded directly from the portal. For a list of suggested uses for this material [CLICK HERE](/uses). Please contact IADC with specific requests or to inquire about Member branding options: [marketing@iadc.org](mailto:marketing@iadc.org).",
  campaign_uses_heading: 'How to Use the Campaign Materials'
};

const CONTENT_FIELDS = [
  { key: 'hero_heading', label: 'Hero Heading', description: 'Large heading displayed over the hero banner image', page: 'Home' },
  { key: 'intro_text', label: 'Intro Text', description: 'Blue text shown below the hero section', multiline: true, page: 'Home' },
  { key: 'toolbox_heading', label: 'Toolbox Heading', description: 'Heading above the media filter bar', page: 'Home' },
  { key: 'toolbox_text', label: 'Toolbox Text', description: 'Description text below the toolbox heading', multiline: true, page: 'Home' },
  { key: 'campaign_uses_heading', label: 'Campaign Page Heading', description: 'Heading displayed over the hero banner on the Campaign Materials page', page: 'Campaign Materials' }
];

const renderTextWithLinks = (text) => {
  if (!text) return null;
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const [, linkText, url] = match;
    parts.push(
      url.startsWith('/') ? (
        <Link key={match.index} as={RouterLink} to={url} color="brand.ltRed" fontWeight="700" fontSize="0.85em" textDecoration="underline" _hover={{ color: 'brand.dkRed' }}>
          {linkText}
        </Link>
      ) : (
        <Link key={match.index} href={url} color="brand.ltRed" fontWeight="700" fontSize="0.85em" textDecoration="underline" _hover={{ color: 'brand.dkRed' }} isExternal={!url.startsWith('mailto:')}>
          {linkText}
        </Link>
      )
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 1 ? parts : text;
};

export const AdminPage = () => {
  const user = useRecoilValue(userAtom);
  const toast = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [activeSection, setActiveSection] = useState('dashboard');
  const [dragActive, setDragActive] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [bulkTopicIds, setBulkTopicIds] = useState([]);
  const [bulkMediaType, setBulkMediaType] = useState('');
  const [bulkDimensions, setBulkDimensions] = useState({ width: '', height: '', unit: 'px' });

  const [media, setMedia] = useState([]);
  const [topics, setTopics] = useState([]);
  const [editingMedia, setEditingMedia] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);

  const [users, setUsers] = useState([]);
  const [newTopic, setNewTopic] = useState('');
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'viewer' });
  const [siteContent, setSiteContent] = useState(DEFAULT_CONTENT);
  const [editingField, setEditingField] = useState(null);
  const [fieldDraft, setFieldDraft] = useState('');
  const [unregisteredFiles, setUnregisteredFiles] = useState([]);
  const [unregEdits, setUnregEdits] = useState({});
  const [registeringFile, setRegisteringFile] = useState(null);
  const { isOpen: isUnregOpen, onOpen: onUnregOpen, onClose: onUnregClose } = useDisclosure();

  const { isOpen: isMediaModalOpen, onOpen: onMediaModalOpen, onClose: onMediaModalClose } = useDisclosure();
  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const { isOpen: isDuplicateOpen, onOpen: onDuplicateOpen, onClose: onDuplicateClose } = useDisclosure();

  const [confirmAction, setConfirmAction] = useState({ title: '', message: '', onConfirm: null });
  const [duplicateItems, setDuplicateItems] = useState([]);
  const [cleanItems, setCleanItems] = useState([]);
  const [reprocessing, setReprocessing] = useState(false);

  const isAdmin = user?.role === 'admin';

  const showConfirm = (title, message, onConfirm) => {
    setConfirmAction({ title, message, onConfirm });
    onConfirmOpen();
  };

  const handleConfirm = () => {
    if (confirmAction.onConfirm) {
      confirmAction.onConfirm();
    }
    onConfirmClose();
  };

  const handleReprocessDimensions = async () => {
    setReprocessing(true);
    try {
      const result = await backfillDimensions();
      const status = result.skipped?.length ? 'warning' : 'success';
      const description = result.skipped?.length
        ? `Skipped: ${result.skipped.slice(0, 3).map(s => s.reason).join(', ')}`
        : undefined;
      toast({ title: result.message, description, status, duration: 8000, isClosable: true });
      if (result.skipped?.length) console.log('Backfill skipped:', result.skipped);
      await fetchAllMedia();
    } catch (err) {
      toast({ title: 'Failed to reprocess dimensions', status: 'error', duration: 4000 });
    }
    setReprocessing(false);
  };

  const fetchSiteContent = useCallback(async () => {
    try {
      const data = await getSiteContent();
      setSiteContent((prev) => ({ ...prev, ...data }));
    } catch (err) {
      console.error('Failed to load site content');
    }
  }, []);

  const handleContentSave = useCallback(async (key) => {
    const trimmed = fieldDraft.trim();
    if (!trimmed || trimmed === siteContent[key]) {
      setEditingField(null);
      return;
    }
    try {
      await updateSiteContent(key, trimmed);
      setSiteContent((prev) => ({ ...prev, [key]: trimmed }));
      toast({ title: 'Content saved', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: 'Failed to save', description: err.response?.data?.error || err.message, status: 'error' });
    }
    setEditingField(null);
  }, [fieldDraft, siteContent, toast]);

  const startEditing = useCallback((key) => {
    setEditingField(key);
    setFieldDraft(siteContent[key] || '');
  }, [siteContent]);

  const checkUnregisteredFiles = useCallback(async () => {
    try {
      const files = await getUnregisteredFiles();
      if (files.length > 0) {
        setUnregisteredFiles(files);
        const edits = {};
        files.forEach((f) => {
          edits[f.filename] = {
            title: f.suggestedTitle,
            mediaType: f.suggestedType,
            topicIds: []
          };
        });
        setUnregEdits(edits);
        onUnregOpen();
      }
    } catch (err) {
      console.error('Failed to check unregistered files:', err);
    }
  }, [onUnregOpen]);

  const handleRegisterFile = async (file) => {
    const edit = unregEdits[file.filename];
    setRegisteringFile(file.filename);
    try {
      await registerMedia({
        filename: file.filename,
        title: edit.title || file.suggestedTitle,
        mediaType: edit.mediaType,
        topicIds: edit.topicIds
      });
      toast({ title: `"${edit.title || file.suggestedTitle}" registered`, status: 'success' });
      setUnregisteredFiles((prev) => prev.filter((f) => f.filename !== file.filename));
      refresh();
    } catch (err) {
      toast({
        title: 'Registration failed',
        description: err.response?.data?.error || 'Unknown error',
        status: 'error'
      });
    }
    setRegisteringFile(null);
  };

  const handleRegisterAll = async () => {
    for (const file of unregisteredFiles) {
      await handleRegisterFile(file);
    }
    onUnregClose();
  };

  const handleDeleteUnregistered = async (file) => {
    setRegisteringFile(file.filename);
    try {
      await deleteUnregisteredFile(file.filename);
      toast({ title: `"${file.filename}" deleted`, status: 'info' });
      setUnregisteredFiles((prev) => prev.filter((f) => f.filename !== file.filename));
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err.response?.data?.error || 'Unknown error',
        status: 'error'
      });
    }
    setRegisteringFile(null);
  };

  const fetchAllMedia = useCallback(async () => {
    try {
      const response = await getMedia({ limit: 1000 });
      setMedia(response.media);
    } catch (err) {
      console.error('Failed to fetch media:', err);
    }
  }, []);

  const fetchAllTopics = useCallback(async () => {
    try {
      const data = await getTopics();
      setTopics(data);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
    }
  }, []);

  const refresh = useCallback(() => {
    fetchAllMedia();
    fetchAllTopics();
  }, [fetchAllMedia, fetchAllTopics]);

  useEffect(() => {
    fetchAllMedia();
    fetchAllTopics();
  }, [fetchAllMedia, fetchAllTopics]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      checkUnregisteredFiles();
      fetchSiteContent();
    }
  }, [isAdmin, checkUnregisteredFiles, fetchSiteContent]);

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = [...e.dataTransfer.files].filter(
      (f) => f.type.startsWith('image/') || f.type.startsWith('video/') || f.type === 'application/pdf'
    );
    if (files.length) {
      addFilesToUpload(files);
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = [...e.target.files];
    if (files.length) {
      addFilesToUpload(files);
    }
  };

  const addFilesToUpload = (files) => {
    const newFiles = files.map((file) => {
      const isVideo = file.type.startsWith('video/');
      const isPdf = file.type === 'application/pdf';
      const entry = {
        id: `${Date.now()}-${Math.random()}`,
        file,
        title: file.name.replace(/\.[^/.]+$/, ''),
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        isVideo,
        isPdf,
        mediaType: isVideo ? 'video' : 'digital',
        thumbnail: null,
        thumbnailPreview: null,
        dimensions: null
      };

      // Detect image dimensions client-side
      if (file.type.startsWith('image/')) {
        const img = new window.Image();
        img.onload = () => {
          setUploadFiles((prev) =>
            prev.map((f) =>
              f.id === entry.id ? { ...f, dimensions: { width: img.width, height: img.height } } : f
            )
          );
          URL.revokeObjectURL(img.src);
        };
        img.src = URL.createObjectURL(file);
      }

      return entry;
    });
    setUploadFiles((prev) => [...prev, ...newFiles]);
  };

  const handleThumbnailSelect = (fileId, e) => {
    const thumbFile = e.target.files?.[0];
    if (thumbFile && thumbFile.type.startsWith('image/')) {
      setUploadFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, thumbnail: thumbFile, thumbnailPreview: URL.createObjectURL(thumbFile) }
            : f
        )
      );
    }
  };

  const removeUploadFile = (id) => {
    setUploadFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const updateFileTitle = (id, title) => {
    setUploadFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, title } : f))
    );
  };

  const updateFileMediaType = (id, mediaType) => {
    setUploadFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, mediaType } : f))
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
  };

  const findDuplicates = (files) => {
    const dupes = [];
    const clean = [];
    for (const item of files) {
      const ext = item.file.name.split('.').pop().toLowerCase();
      const match = media.find(
        (m) => m.title === item.title && Number(m.fileSize) === item.file.size && m.fileType === ext
      );
      if (match) {
        dupes.push({ ...item, existingMatch: match });
      } else {
        clean.push(item);
      }
    }
    return { dupes, clean };
  };

  const executeUploads = async (items) => {
    for (const item of items) {
      setUploadProgress((prev) => ({ ...prev, [item.id]: 0 }));

      try {
        const formData = new FormData();
        formData.append('file', item.file);
        formData.append('title', item.title);
        formData.append('mediaType', item.mediaType);
        if (selectedTopics.length) {
          formData.append('topicIds', JSON.stringify(selectedTopics));
        }
        if (item.thumbnail) {
          formData.append('thumbnail', item.thumbnail);
        }

        setUploadProgress((prev) => ({ ...prev, [item.id]: 50 }));
        await uploadMedia(formData);
        setUploadProgress((prev) => ({ ...prev, [item.id]: 100 }));

        setTimeout(() => {
          setUploadFiles((prev) => prev.filter((f) => f.id !== item.id));
          setUploadProgress((prev) => {
            const next = { ...prev };
            delete next[item.id];
            return next;
          });
        }, 500);
      } catch (err) {
        toast({
          title: `Failed to upload ${item.file.name}`,
          description: err.response?.data?.error || 'Unknown error',
          status: 'error'
        });
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[item.id];
          return next;
        });
      }
    }

    toast({ title: 'Upload complete', status: 'success' });
    refresh();
  };

  const handleUploadAll = async () => {
    const { dupes, clean } = findDuplicates(uploadFiles);
    if (dupes.length > 0) {
      setDuplicateItems(dupes.map((d) => ({ ...d, include: true, customTitle: d.title })));
      setCleanItems(clean);
      onDuplicateOpen();
      return;
    }
    await executeUploads(uploadFiles);
  };

  const handleDuplicateConfirm = async () => {
    onDuplicateClose();
    const confirmed = duplicateItems
      .filter((d) => d.include)
      .map(({ existingMatch, include, customTitle, ...rest }) => ({ ...rest, title: customTitle }));
    const allItems = [...cleanItems, ...confirmed];
    if (allItems.length > 0) {
      await executeUploads(allItems);
    }
    setDuplicateItems([]);
    setCleanItems([]);
  };

  const handleEditMedia = (item) => {
    setEditingMedia({ ...item, topicIds: (item.topics || []).map(t => t.id), mediaType: item.mediaType, newThumbnail: null, newThumbnailPreview: null });
    onMediaModalOpen();
  };

  const handlePreviewMedia = (item) => {
    setPreviewMedia(item);
    onPreviewOpen();
  };

  const handleUpdateMedia = async () => {
    try {
      let data;
      if (editingMedia.newThumbnail) {
        data = new FormData();
        data.append('title', editingMedia.title);
        data.append('mediaType', editingMedia.mediaType);
        data.append('topicIds', JSON.stringify(editingMedia.topicIds || []));
        data.append('thumbnail', editingMedia.newThumbnail);
        if (editingMedia.width !== '' && editingMedia.width != null) data.append('width', editingMedia.width);
        if (editingMedia.height !== '' && editingMedia.height != null) data.append('height', editingMedia.height);
        data.append('dimensionUnit', editingMedia.dimensionUnit || 'px');
      } else {
        data = {
          title: editingMedia.title,
          mediaType: editingMedia.mediaType,
          topicIds: editingMedia.topicIds || [],
          ...(editingMedia.width !== '' && editingMedia.width != null && { width: editingMedia.width }),
          ...(editingMedia.height !== '' && editingMedia.height != null && { height: editingMedia.height }),
          dimensionUnit: editingMedia.dimensionUnit || 'px'
        };
      }
      await updateMedia(editingMedia.id, data);
      toast({ title: 'Media updated', status: 'success' });
      onMediaModalClose();
      setEditingMedia(null);
      refresh();
    } catch (err) {
      toast({
        title: 'Update failed',
        description: err.response?.data?.error || 'Unknown error',
        status: 'error'
      });
    }
  };

  const handleDeleteMedia = (uuid) => {
    showConfirm('Delete Media', 'Are you sure you want to delete this media?', async () => {
      try {
        await deleteMedia(uuid);
        toast({ title: 'Media deleted', status: 'success' });
        refresh();
      } catch (err) {
        toast({
          title: 'Delete failed',
          description: err.response?.data?.error || 'Unknown error',
          status: 'error'
        });
      }
    });
  };

  const handleBulkDelete = () => {
    showConfirm('Delete Selected', `Are you sure you want to delete ${selectedMedia.length} items?`, async () => {
      for (const id of selectedMedia) {
        try {
          await deleteMedia(id);
        } catch (err) {
          console.error('Failed to delete:', id);
        }
      }
      toast({ title: 'Items deleted', status: 'success' });
      setSelectedMedia([]);
      refresh();
    });
  };

  const handleBulkTopics = async (onClose) => {
    for (const id of selectedMedia) {
      try {
        await updateMedia(id, { topicIds: bulkTopicIds });
      } catch (err) {
        console.error('Failed to update topics:', id);
      }
    }
    toast({ title: 'Topics updated', status: 'success' });
    setSelectedMedia([]);
    setBulkTopicIds([]);
    onClose();
    refresh();
  };

  const handleBulkMediaType = async (onClose) => {
    if (!bulkMediaType) return;
    for (const id of selectedMedia) {
      try {
        await updateMedia(id, { mediaType: bulkMediaType });
      } catch (err) {
        console.error('Failed to update media type:', id);
      }
    }
    toast({ title: 'Media type updated', status: 'success' });
    setSelectedMedia([]);
    setBulkMediaType('');
    onClose();
    refresh();
  };

  const handleBulkDimensions = async (onClose) => {
    const { width, height, unit } = bulkDimensions;
    if (!width && !height) return;
    const payload = {
      ...(width !== '' && { width: parseFloat(width) }),
      ...(height !== '' && { height: parseFloat(height) }),
      dimensionUnit: unit
    };
    for (const id of selectedMedia) {
      try {
        await updateMedia(id, payload);
      } catch (err) {
        console.error('Failed to update dimensions:', id);
      }
    }
    toast({ title: 'Dimensions updated', status: 'success' });
    setSelectedMedia([]);
    setBulkDimensions({ width: '', height: '', unit: 'px' });
    onClose();
    refresh();
  };

  const toggleSelectMedia = (id) => {
    setSelectedMedia((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedMedia.length === media.length) {
      setSelectedMedia([]);
    } else {
      setSelectedMedia(media.map((m) => m.id));
    }
  };

  const handleCreateTopic = async (e) => {
    e.preventDefault();
    if (!newTopic.trim()) return;

    try {
      await createTopic(newTopic);
      toast({ title: 'Topic created', status: 'success' });
      setNewTopic('');
      refresh();
    } catch (err) {
      toast({
        title: 'Create failed',
        description: err.response?.data?.error || 'Unknown error',
        status: 'error'
      });
    }
  };

  const handleUpdateTopic = async (id, name) => {
    try {
      await updateTopic(id, name);
      toast({ title: 'Topic updated', status: 'success' });
      setEditingTopic(null);
      refresh();
    } catch (err) {
      toast({
        title: 'Update failed',
        description: err.response?.data?.error || 'Unknown error',
        status: 'error'
      });
    }
  };

  const handleDeleteTopic = (id) => {
    showConfirm('Delete Topic', 'Are you sure you want to delete this topic?', async () => {
      try {
        await deleteTopic(id);
        toast({ title: 'Topic deleted', status: 'success' });
        refresh();
      } catch (err) {
        toast({
          title: 'Delete failed',
          description: err.response?.data?.error || 'Unknown error',
          status: 'error'
        });
      }
    });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast({ title: 'All fields required', status: 'warning' });
      return;
    }

    try {
      await createUser(newUser);
      toast({ title: 'User created', status: 'success' });
      setNewUser({ email: '', password: '', name: '', role: 'viewer' });
      fetchUsers();
    } catch (err) {
      toast({
        title: 'Create failed',
        description: err.response?.data?.error || 'Unknown error',
        status: 'error'
      });
    }
  };

  const handleEditUser = (u) => {
    setEditingUser({ ...u, password: '' });
    onUserModalOpen();
  };

  const handleUpdateUser = async () => {
    try {
      const data = {
        email: editingUser.email,
        name: editingUser.name,
        role: editingUser.role,
        isActive: editingUser.isActive
      };
      if (editingUser.password) {
        data.password = editingUser.password;
      }
      await updateUser(editingUser.id, data);
      toast({ title: 'User updated', status: 'success' });
      onUserModalClose();
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast({
        title: 'Update failed',
        description: err.response?.data?.error || 'Unknown error',
        status: 'error'
      });
    }
  };

  const handleDeleteUser = (id) => {
    showConfirm('Delete User', 'Are you sure you want to delete this user?', async () => {
      try {
        await deleteUser(id);
        toast({ title: 'User deleted', status: 'success' });
        fetchUsers();
      } catch (err) {
        toast({
          title: 'Delete failed',
          description: err.response?.data?.error || 'Unknown error',
          status: 'error'
        });
      }
    });
  };

  const stats = {
    total: media.length,
    videos: media.filter((m) => m.mediaType === 'video').length,
    social: media.filter((m) => m.mediaType === 'social').length,
    print: media.filter((m) => m.mediaType === 'print').length,
    digital: media.filter((m) => m.mediaType === 'digital').length,
    topics: topics.length,
    users: users.length
  };

  const filteredNav = NAV_ITEMS.filter((item) => item.roles.includes(user?.role));

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <VStack spacing={8} align="stretch">
            <Heading size="lg" color="brand.dkGray2">Dashboard</Heading>

            <StatGroup>
              <Stat bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <StatLabel color="brand.dkGray2">Total Media</StatLabel>
                <StatNumber color="brand.dkBlue" fontSize="3xl">{stats.total}</StatNumber>
              </Stat>
              <Stat bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <HStack>
                  <Icon as={FaVideo} color="brand.ltRed" />
                  <StatLabel color="brand.dkGray2">Video</StatLabel>
                </HStack>
                <StatNumber color="brand.dkBlue" fontSize="3xl">{stats.videos}</StatNumber>
              </Stat>
              <Stat bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <StatLabel color="brand.dkGray2">Social</StatLabel>
                <StatNumber color="brand.dkBlue" fontSize="3xl">{stats.social}</StatNumber>
              </Stat>
              <Stat bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <StatLabel color="brand.dkGray2">Print</StatLabel>
                <StatNumber color="brand.dkBlue" fontSize="3xl">{stats.print}</StatNumber>
              </Stat>
              <Stat bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <StatLabel color="brand.dkGray2">Digital</StatLabel>
                <StatNumber color="brand.dkBlue" fontSize="3xl">{stats.digital}</StatNumber>
              </Stat>
              <Stat bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <HStack>
                  <Icon as={FaTags} color="brand.ltGreen" />
                  <StatLabel color="brand.dkGray2">Topics</StatLabel>
                </HStack>
                <StatNumber color="brand.dkBlue" fontSize="3xl">{stats.topics}</StatNumber>
              </Stat>
              {isAdmin && (
                <Stat bg="white" p={6} borderRadius="lg" boxShadow="sm">
                  <HStack>
                    <Icon as={FaUsers} color="brand.dkPurple" />
                    <StatLabel color="brand.dkGray2">Users</StatLabel>
                  </HStack>
                  <StatNumber color="brand.dkBlue" fontSize="3xl">{stats.users}</StatNumber>
                </Stat>
              )}
            </StatGroup>

            <Box>
              <HStack justify="space-between" mb={4}>
                <Heading size="md" color="brand.dkGray2">Recent Media</Heading>
                <Button size="sm" variant="outline" colorScheme="blue" onClick={() => setActiveSection('media')}>
                  View All
                </Button>
              </HStack>
              <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4}>
                {media.slice(0, 6).map((item) => {
                  const isVideo = item.mediaType === 'video';
                  const isPdf = item.fileType === 'pdf';
                  const displayUrl = item.thumbnailPath || (!isVideo && !isPdf ? item.filePath : null);
                  return (
                    <Box
                      key={item.id}
                      bg="white"
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="sm"
                      cursor="pointer"
                      onClick={() => handlePreviewMedia(item)}
                      _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
                      transition="all 0.2s"
                    >
                      <Box position="relative" paddingTop="100%" bg="brand.ltGray2">
                        {displayUrl ? (
                          <Image
                            src={displayUrl}
                            alt={item.title}
                            position="absolute"
                            top={0}
                            left={0}
                            w="100%"
                            h="100%"
                            objectFit="cover"
                            objectPosition="center top"
                          />
                        ) : (
                          <Flex
                            position="absolute"
                            top={0}
                            left={0}
                            w="100%"
                            h="100%"
                            align="center"
                            justify="center"
                            bg="brand.dkGray2"
                          >
                            <Icon as={isPdf ? FaFilePdf : FaVideo} boxSize={8} color={isPdf ? 'red.300' : 'white'} />
                          </Flex>
                        )}
                        {isVideo && (
                          <Badge position="absolute" top={2} right={2} colorScheme="red">
                            VIDEO
                          </Badge>
                        )}
                        {isPdf && (
                          <Badge position="absolute" top={2} right={2} colorScheme="orange">
                            PDF
                          </Badge>
                        )}
                      </Box>
                      <Text p={2} fontSize="xs" noOfLines={1} color="brand.dkGray2">
                        {item.title}
                      </Text>
                    </Box>
                  );
                })}
              </SimpleGrid>
            </Box>
          </VStack>
        );

      case 'upload':
        return (
          <VStack spacing={6} align="stretch">
            <Heading size="lg" color="brand.dkGray2">Upload Media</Heading>

            <Box
              border="3px dashed"
              borderColor={dragActive ? 'brand.ltBlue' : 'brand.dkGray1'}
              borderRadius="xl"
              p={12}
              textAlign="center"
              bg={dragActive ? 'blue.50' : 'white'}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              cursor="pointer"
              onClick={() => fileInputRef.current?.click()}
              transition="all 0.2s"
              _hover={{ borderColor: 'brand.ltBlue', bg: 'blue.50' }}
            >
              <Icon as={FaCloudUploadAlt} boxSize={16} color="brand.dkGray1" mb={4} />
              <Text fontSize="xl" fontWeight="600" color="brand.dkGray2" mb={2}>
                Drag & drop files here
              </Text>
              <Text color="brand.dkGray1">
                or click to browse
              </Text>
              <Text fontSize="sm" color="brand.dkGray1" mt={2}>
                Supports: PNG, JPG, GIF, MP4, MOV, PDF
              </Text>
              <Text fontSize="xs" color="brand.dkGray1" mt={1}>
                For print: 2000px+ wide, 5MB+ | For web: 1200px+ wide
              </Text>
              <Input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,.pdf,application/pdf"
                display="none"
                onChange={handleFileSelect}
              />
            </Box>

            {uploadFiles.length > 0 && (
              <Box bg="white" borderRadius="lg" p={6} boxShadow="sm">
                <HStack justify="space-between" mb={4}>
                  <Heading size="sm" color="brand.dkGray2">
                    {uploadFiles.length} file(s) ready
                  </Heading>
                  <HStack>
                    <Wrap spacing={2} align="center">
                      {topics.map((t) => (
                        <WrapItem key={t.id}>
                          <Checkbox
                            size="sm"
                            colorScheme="blue"
                            isChecked={selectedTopics.includes(t.id)}
                            onChange={(e) => {
                              setSelectedTopics((prev) =>
                                e.target.checked
                                  ? [...prev, t.id]
                                  : prev.filter((id) => id !== t.id)
                              );
                            }}
                          >
                            <Text fontSize="sm">{t.name}</Text>
                          </Checkbox>
                        </WrapItem>
                      ))}
                    </Wrap>
                    <Button
                      bg="brand.ltRed"
                      color="white"
                      _hover={{ bg: 'brand.dkRed' }}
                      onClick={handleUploadAll}
                      leftIcon={<Icon as={FaCloudUploadAlt} />}
                    >
                      Upload All
                    </Button>
                  </HStack>
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {uploadFiles.map((item) => (
                    <Box
                      key={item.id}
                      border="1px solid"
                      borderColor="brand.ltGray2"
                      borderRadius="lg"
                      overflow="hidden"
                    >
                      <Box position="relative" paddingTop="60%" bg="brand.ltGray2">
                        {item.preview || item.thumbnailPreview ? (
                          <Image
                            src={item.thumbnailPreview || item.preview}
                            alt={item.title}
                            position="absolute"
                            top={0}
                            left={0}
                            w="100%"
                            h="100%"
                            objectFit="cover"
                            objectPosition="center top"
                          />
                        ) : (
                          <Flex
                            position="absolute"
                            top={0}
                            left={0}
                            w="100%"
                            h="100%"
                            align="center"
                            justify="center"
                          >
                            <Icon as={item.isPdf ? FaFilePdf : FaVideo} boxSize={10} color={item.isPdf ? 'red.500' : 'brand.dkGray1'} />
                          </Flex>
                        )}
                        <IconButton
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          position="absolute"
                          top={2}
                          right={2}
                          onClick={() => removeUploadFile(item.id)}
                          aria-label="Remove"
                        />
                        {item.isVideo && (
                          <Badge position="absolute" top={2} left={2} colorScheme="red" fontSize="xs">
                            VIDEO
                          </Badge>
                        )}
                        {item.isPdf && (
                          <Badge position="absolute" top={2} left={2} colorScheme="orange" fontSize="xs">
                            PDF
                          </Badge>
                        )}
                      </Box>
                      <Box p={3}>
                        <Input
                          size="sm"
                          value={item.title}
                          onChange={(e) => updateFileTitle(item.id, e.target.value)}
                          placeholder="Title"
                          mb={2}
                        />
                        <Select
                          size="sm"
                          value={item.mediaType}
                          onChange={(e) => updateFileMediaType(item.id, e.target.value)}
                          mb={2}
                        >
                          {MEDIA_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </Select>
                        <Box mb={2}>
                          <Input
                            type="file"
                            accept="image/*"
                            display="none"
                            id={`thumb-${item.id}`}
                            onChange={(e) => handleThumbnailSelect(item.id, e)}
                          />
                          <HStack spacing={2}>
                            <Button
                              as="label"
                              htmlFor={`thumb-${item.id}`}
                              size="xs"
                              variant="outline"
                              colorScheme={item.thumbnail ? 'green' : 'gray'}
                              cursor="pointer"
                              leftIcon={<Icon as={FaImage} />}
                            >
                              {item.thumbnail ? 'Thumb Added' : '+ Thumbnail'}
                            </Button>
                            {!item.isVideo && !item.thumbnail && (
                              <Text fontSize="xs" color="brand.dkGray1">Auto-generated if not provided</Text>
                            )}
                          </HStack>
                          {item.dimensions && (
                            <Text
                              fontSize="xs"
                              mt={1}
                              color={item.dimensions.width >= 2000 ? 'green.500' : 'orange.500'}
                            >
                              {item.dimensions.width} x {item.dimensions.height}px
                            </Text>
                          )}
                        </Box>
                        {uploadProgress[item.id] !== undefined && (
                          <Progress
                            value={uploadProgress[item.id]}
                            size="sm"
                            colorScheme="green"
                            borderRadius="full"
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            )}

          </VStack>
        );

      case 'media':
        return (
          <VStack spacing={6} align="stretch">
            <HStack justify="space-between">
              <HStack spacing={4}>
                <Heading size="lg" color="brand.dkGray2">Media Library</Heading>
                {media.length > 0 && (
                  <Checkbox
                    isChecked={selectedMedia.length === media.length && media.length > 0}
                    isIndeterminate={selectedMedia.length > 0 && selectedMedia.length < media.length}
                    onChange={toggleSelectAll}
                    colorScheme="blue"
                  >
                    <Text fontSize="sm" color="brand.dkGray2">Select All</Text>
                  </Checkbox>
                )}
              </HStack>
              <HStack spacing={2}>
                {isAdmin && (
                  <Button
                    leftIcon={<Icon as={FaSyncAlt} />}
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={handleReprocessDimensions}
                    isLoading={reprocessing}
                    loadingText="Reprocessing..."
                  >
                    Reprocess Dimensions
                  </Button>
                )}
                {selectedMedia.length > 0 && (
                  <>
                    <Popover placement="bottom-end" onClose={() => setBulkTopicIds([])}>
                      {({ onClose }) => (
                        <>
                          <PopoverTrigger>
                            <Button size="sm" variant="outline" colorScheme="blue" leftIcon={<Icon as={FaTags} />}>
                              Set Topics
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent bg="white" w="280px">
                            <PopoverHeader fontWeight="bold" borderColor="brand.ltGray2">Set Topics ({selectedMedia.length} items)</PopoverHeader>
                            <PopoverCloseButton />
                            <PopoverBody maxH="200px" overflowY="auto">
                              <CheckboxGroup value={bulkTopicIds} onChange={(vals) => setBulkTopicIds(vals.map(Number))}>
                                <VStack align="start" spacing={2}>
                                  {topics.map((t) => (
                                    <Checkbox key={t.id} value={t.id} colorScheme="blue">
                                      <Text fontSize="sm">{t.name}</Text>
                                    </Checkbox>
                                  ))}
                                </VStack>
                              </CheckboxGroup>
                            </PopoverBody>
                            <PopoverFooter borderColor="brand.ltGray2">
                              <Button size="sm" colorScheme="blue" onClick={() => handleBulkTopics(onClose)}>
                                Apply
                              </Button>
                            </PopoverFooter>
                          </PopoverContent>
                        </>
                      )}
                    </Popover>
                    <Popover placement="bottom-end" onClose={() => setBulkMediaType('')}>
                      {({ onClose }) => (
                        <>
                          <PopoverTrigger>
                            <Button size="sm" variant="outline" colorScheme="blue" leftIcon={<Icon as={FaImage} />}>
                              Set Type
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent bg="white" w="240px">
                            <PopoverHeader fontWeight="bold" borderColor="brand.ltGray2">Set Media Type ({selectedMedia.length} items)</PopoverHeader>
                            <PopoverCloseButton />
                            <PopoverBody>
                              <Select
                                placeholder="Select type"
                                value={bulkMediaType}
                                onChange={(e) => setBulkMediaType(e.target.value)}
                                borderColor="gray.400"
                                size="sm"
                              >
                                {MEDIA_TYPE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </Select>
                            </PopoverBody>
                            <PopoverFooter borderColor="brand.ltGray2">
                              <Button size="sm" colorScheme="blue" onClick={() => handleBulkMediaType(onClose)} isDisabled={!bulkMediaType}>
                                Apply
                              </Button>
                            </PopoverFooter>
                          </PopoverContent>
                        </>
                      )}
                    </Popover>
                    <Popover placement="bottom-end" onClose={() => setBulkDimensions({ width: '', height: '', unit: 'px' })}>
                      {({ onClose }) => (
                        <>
                          <PopoverTrigger>
                            <Button size="sm" variant="outline" colorScheme="blue" leftIcon={<Icon as={FaEdit} />}>
                              Set Dimensions
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent bg="white" w="280px">
                            <PopoverHeader fontWeight="bold" borderColor="brand.ltGray2">Set Dimensions ({selectedMedia.length} items)</PopoverHeader>
                            <PopoverCloseButton />
                            <PopoverBody>
                              <Flex gap={2} align="center">
                                <Input
                                  type="number"
                                  step="any"
                                  placeholder="Width"
                                  size="sm"
                                  value={bulkDimensions.width}
                                  onChange={(e) => setBulkDimensions({ ...bulkDimensions, width: e.target.value })}
                                  borderColor="gray.400"
                                />
                                <Text fontSize="sm">x</Text>
                                <Input
                                  type="number"
                                  step="any"
                                  placeholder="Height"
                                  size="sm"
                                  value={bulkDimensions.height}
                                  onChange={(e) => setBulkDimensions({ ...bulkDimensions, height: e.target.value })}
                                  borderColor="gray.400"
                                />
                                <Select
                                  size="sm"
                                  w="80px"
                                  flexShrink={0}
                                  value={bulkDimensions.unit}
                                  onChange={(e) => setBulkDimensions({ ...bulkDimensions, unit: e.target.value })}
                                  borderColor="gray.400"
                                >
                                  <option value="px">px</option>
                                  <option value="in">in</option>
                                </Select>
                              </Flex>
                            </PopoverBody>
                            <PopoverFooter borderColor="brand.ltGray2">
                              <Button size="sm" colorScheme="blue" onClick={() => handleBulkDimensions(onClose)} isDisabled={!bulkDimensions.width && !bulkDimensions.height}>
                                Apply
                              </Button>
                            </PopoverFooter>
                          </PopoverContent>
                        </>
                      )}
                    </Popover>
                    {isAdmin && (
                      <Button
                        colorScheme="red"
                        size="sm"
                        leftIcon={<DeleteIcon />}
                        onClick={handleBulkDelete}
                      >
                        Delete {selectedMedia.length}
                      </Button>
                    )}
                  </>
                )}
                <Button
                  bg="brand.ltRed"
                  color="white"
                  size="sm"
                  leftIcon={<AddIcon />}
                  _hover={{ bg: 'brand.dkRed' }}
                  onClick={() => setActiveSection('upload')}
                >
                  Upload
                </Button>
              </HStack>
            </HStack>

            {media.length === 0 ? (
              <Box bg="white" p={12} borderRadius="lg" textAlign="center">
                <Icon as={FaFolder} boxSize={16} color="brand.dkGray1" mb={4} />
                <Text color="brand.dkGray2">No media found</Text>
                <Button
                  mt={4}
                  bg="brand.ltRed"
                  color="white"
                  _hover={{ bg: 'brand.dkRed' }}
                  onClick={() => setActiveSection('upload')}
                >
                  Upload Media
                </Button>
              </Box>
            ) : (
              <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 6 }} spacing={4}>
                {media.map((item) => {
                  const isVideo = item.mediaType === 'video';
                  const isPdf = item.fileType === 'pdf';
                  const displayUrl = item.thumbnailPath || (!isVideo && !isPdf ? item.filePath : null);
                  return (
                    <Box
                      key={item.id}
                      bg="white"
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="sm"
                      border="2px solid"
                      borderColor={selectedMedia.includes(item.id) ? 'brand.ltBlue' : 'transparent'}
                      transition="all 0.2s"
                      _hover={{ boxShadow: 'md' }}
                    >
                      <Box position="relative" paddingTop="100%" bg="brand.ltGray2">
                        {displayUrl ? (
                          <Image
                            src={displayUrl}
                            alt={item.title}
                            position="absolute"
                            top={0}
                            left={0}
                            w="100%"
                            h="100%"
                            objectFit="cover"
                            objectPosition="center top"
                            cursor="pointer"
                            onClick={() => handlePreviewMedia(item)}
                          />
                        ) : (
                          <Flex
                            position="absolute"
                            top={0}
                            left={0}
                            w="100%"
                            h="100%"
                            align="center"
                            justify="center"
                            bg="brand.dkGray2"
                            cursor="pointer"
                            onClick={() => handlePreviewMedia(item)}
                          >
                            <Icon as={isPdf ? FaFilePdf : FaVideo} boxSize={10} color={isPdf ? 'red.300' : 'white'} />
                          </Flex>
                        )}
                        {isVideo && (
                          <Badge position="absolute" top={2} left={2} colorScheme="red" fontSize="xs">
                            VIDEO
                          </Badge>
                        )}
                        {isPdf && (
                          <Badge position="absolute" top={2} left={2} colorScheme="orange" fontSize="xs">
                            PDF
                          </Badge>
                        )}
                        <Checkbox
                          position="absolute"
                          top={2}
                          right={2}
                          isChecked={selectedMedia.includes(item.id)}
                          onChange={() => toggleSelectMedia(item.id)}
                          colorScheme="blue"
                          bg="white"
                          borderRadius="sm"
                        />
                      </Box>
                      <Box p={3}>
                        <Text fontSize="sm" fontWeight="500" noOfLines={1} color="brand.dkGray2" mb={1}>
                          {item.title}
                        </Text>
                        <HStack justify="space-between" fontSize="xs" color="brand.dkGray1">
                          <Text>{item.fileType?.toUpperCase()}</Text>
                          <HStack spacing={1}>
                            <IconButton
                              icon={<ViewIcon />}
                              size="xs"
                              variant="ghost"
                              onClick={() => handlePreviewMedia(item)}
                              aria-label="Preview"
                            />
                            <IconButton
                              icon={<EditIcon />}
                              size="xs"
                              variant="ghost"
                              colorScheme="blue"
                              onClick={() => handleEditMedia(item)}
                              aria-label="Edit"
                            />
                            {isAdmin && (
                              <IconButton
                                icon={<DeleteIcon />}
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => handleDeleteMedia(item.id)}
                                aria-label="Delete"
                              />
                            )}
                          </HStack>
                        </HStack>
                      </Box>
                    </Box>
                  );
                })}
              </SimpleGrid>
            )}
          </VStack>
        );

      case 'homepage':
        return (
          <VStack spacing={6} align="stretch">
            <HStack justify="space-between">
              <Heading size="lg" color="brand.dkGray2">Website Text</Heading>
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={() => showConfirm(
                  'Revert Site Content',
                  'This will reset all editable website text back to the original defaults. Are you sure?',
                  async () => {
                    try {
                      const result = await revertSiteContent();
                      setSiteContent((prev) => ({ ...prev, ...result.content }));
                      toast({ title: result.message || 'Content reverted to defaults', status: 'success', duration: 4000 });
                    } catch (err) {
                      toast({ title: 'Failed to revert content', status: 'error', duration: 4000 });
                    }
                  }
                )}
              >
                Revert to Defaults
              </Button>
            </HStack>

            {CONTENT_FIELDS.map((field) => (
              <Box key={field.key} bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <HStack spacing={2} mb={1}>
                  <Text fontSize="xs" fontWeight="700" textTransform="uppercase" color="brand.dkGray1">
                    {field.label}
                  </Text>
                  <Text fontSize="xs" color="brand.dkGray1" fontStyle="italic">
                    — {field.page} page
                  </Text>
                </HStack>
                <Text fontSize="xs" color="brand.dkGray1" mb={field.multiline ? 1 : 3}>
                  {field.description}
                </Text>
                {field.multiline && (
                  <Text fontSize="xs" color="brand.dkGray1" mb={3} fontStyle="italic">
                    Tip: Use [text](url) to add a link, e.g. [CLICK HERE](/uses)
                  </Text>
                )}
                {editingField === field.key ? (
                  <Textarea
                    value={fieldDraft}
                    onChange={(e) => setFieldDraft(e.target.value)}
                    onBlur={() => handleContentSave(field.key)}
                    autoFocus
                    rows={field.multiline ? 4 : 1}
                    resize="vertical"
                    border="1px dashed"
                    borderColor="brand.ltRed"
                    _focus={{ borderColor: 'brand.ltRed', boxShadow: 'none' }}
                  />
                ) : (
                  <Box
                    p={3}
                    bg="brand.ltGray2"
                    borderRadius="md"
                    cursor="pointer"
                    _hover={{ outline: '1px dashed', outlineColor: 'brand.ltRed', outlineOffset: '2px' }}
                    onClick={() => startEditing(field.key)}
                  >
                    <Text whiteSpace="pre-line" color="brand.dkGray2">
                      {renderTextWithLinks(siteContent[field.key] || DEFAULT_CONTENT[field.key])}
                    </Text>
                  </Box>
                )}
              </Box>
            ))}
          </VStack>
        );

      case 'topics':
        return (
          <VStack spacing={6} align="stretch" maxW="600px">
            <Heading size="lg" color="brand.dkGray2">Topics</Heading>

            <Box as="form" onSubmit={handleCreateTopic} bg="white" p={4} borderRadius="lg" boxShadow="sm">
              <HStack>
                <Input
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                  placeholder="New topic name"
                />
                <Button
                  type="submit"
                  bg="brand.ltRed"
                  color="white"
                  _hover={{ bg: 'brand.dkRed' }}
                  leftIcon={<AddIcon />}
                >
                  Add
                </Button>
              </HStack>
            </Box>

            <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
              <Table>
                <Thead bg="brand.ltGray2">
                  <Tr>
                    <Th>Name</Th>
                    <Th>Slug</Th>
                    <Th w="100px">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {topics.map((t) => (
                    <Tr key={t.id}>
                      <Td>
                        {editingTopic?.id === t.id ? (
                          <Input
                            size="sm"
                            value={editingTopic.name}
                            onChange={(e) =>
                              setEditingTopic({ ...editingTopic, name: e.target.value })
                            }
                            onBlur={() => handleUpdateTopic(t.id, editingTopic.name)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleUpdateTopic(t.id, editingTopic.name);
                              if (e.key === 'Escape') setEditingTopic(null);
                            }}
                            autoFocus
                          />
                        ) : (
                          <Text fontWeight="500">{t.name}</Text>
                        )}
                      </Td>
                      <Td color="brand.dkGray1">{t.slug}</Td>
                      <Td>
                        <HStack spacing={1}>
                          <IconButton
                            icon={<EditIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => setEditingTopic({ id: t.id, name: t.name })}
                            aria-label="Edit"
                          />
                          <IconButton
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDeleteTopic(t.id)}
                            aria-label="Delete"
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </VStack>
        );

      case 'users':
        return (
          <VStack spacing={6} align="stretch">
            <Heading size="lg" color="brand.dkGray2">Users</Heading>

            <Box as="form" onSubmit={handleCreateUser} bg="white" p={6} borderRadius="lg" boxShadow="sm">
              <Heading size="sm" mb={4} color="brand.dkGray2">Create New User</Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Name</FormLabel>
                  <Input
                    size="sm"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="Full name"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Email</FormLabel>
                  <Input
                    size="sm"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="Email"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Password</FormLabel>
                  <Input
                    size="sm"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Password"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Role</FormLabel>
                  <Select
                    size="sm"
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </Select>
                </FormControl>
                <Button
                  type="submit"
                  bg="brand.ltRed"
                  color="white"
                  _hover={{ bg: 'brand.dkRed' }}
                  alignSelf="flex-end"
                  size="sm"
                  leftIcon={<AddIcon />}
                >
                  Create
                </Button>
              </SimpleGrid>
            </Box>

            <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
              <Table>
                <Thead bg="brand.ltGray2">
                  <Tr>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Role</Th>
                    <Th>Status</Th>
                    <Th>Created</Th>
                    <Th w="100px">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {users.map((u) => (
                    <Tr key={u.id}>
                      <Td fontWeight="500">{u.name}</Td>
                      <Td>{u.email}</Td>
                      <Td>
                        <Badge
                          colorScheme={u.role === 'admin' ? 'red' : u.role === 'editor' ? 'blue' : 'gray'}
                        >
                          {u.role}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={u.isActive ? 'green' : 'gray'}>
                          {u.isActive ? 'Active' : 'Disabled'}
                        </Badge>
                      </Td>
                      <Td color="brand.dkGray1" fontSize="sm">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <IconButton
                            icon={<EditIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleEditUser(u)}
                            aria-label="Edit"
                          />
                          {u.id !== user?.id && (
                            <IconButton
                              icon={<DeleteIcon />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => handleDeleteUser(u.id)}
                              aria-label="Delete"
                            />
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </VStack>
        );

      default:
        return null;
    }
  };

  return (
    <Flex minH="calc(100vh - 120px)">
      <Box
        w="240px"
        bg="brand.dkPurple"
        color="white"
        py={6}
        display={{ base: 'none', md: 'block' }}
        flexShrink={0}
      >
        <VStack spacing={1} align="stretch" px={4}>
          <HStack px={3} py={4} mb={4}>
            <Badge colorScheme={isAdmin ? 'red' : 'blue'} fontSize="xs">
              {user?.role?.toUpperCase()}
            </Badge>
            <Text fontSize="sm" fontWeight="500" noOfLines={1}>
              {user?.name}
            </Text>
          </HStack>

          {filteredNav.map((item) => (
            <Box
              key={item.id}
              px={4}
              py={3}
              borderRadius="md"
              cursor="pointer"
              bg={activeSection === item.id ? 'whiteAlpha.200' : 'transparent'}
              _hover={{ bg: 'whiteAlpha.100' }}
              onClick={() => setActiveSection(item.id)}
              transition="all 0.2s"
            >
              <HStack spacing={3}>
                <Icon as={item.icon} />
                <Text fontSize="sm" fontWeight="500">{item.label}</Text>
              </HStack>
            </Box>
          ))}

          <Box h={4} />

          <Box
            px={4}
            py={3}
            borderRadius="md"
            cursor="pointer"
            _hover={{ bg: 'whiteAlpha.100' }}
            onClick={() => navigate('/')}
            transition="all 0.2s"
          >
            <HStack spacing={3}>
              <Icon as={FaHome} />
              <Text fontSize="sm" fontWeight="500">Back to Site</Text>
            </HStack>
          </Box>
        </VStack>
      </Box>

      <Box flex={1} p={{ base: 4, md: 8 }} bg="brand.ltGray1" overflowY="auto">
        {renderContent()}
      </Box>

      <Modal isOpen={isMediaModalOpen} onClose={onMediaModalClose}>
        <ModalOverlay />
        <ModalContent bg="white" sx={{ 'input, select': { borderColor: 'gray.400 !important', '&:hover': { borderColor: 'gray.500 !important' }, '&:focus': { borderColor: 'brand.ltBlue !important' } } }}>
          <ModalHeader>Edit Media</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Title</FormLabel>
                <Input
                  value={editingMedia?.title || ''}
                  onChange={(e) => setEditingMedia({ ...editingMedia, title: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Media Type</FormLabel>
                <Select
                  value={editingMedia?.mediaType || 'digital'}
                  onChange={(e) => setEditingMedia({ ...editingMedia, mediaType: e.target.value })}
                >
                  {MEDIA_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Dimensions</FormLabel>
                <Flex gap={2} align="center">
                  <Input
                    type="number"
                    placeholder="Width"
                    value={editingMedia?.width ?? ''}
                    step="any"
                    onChange={(e) => setEditingMedia({ ...editingMedia, width: e.target.value })}
                  />
                  <Text>x</Text>
                  <Input
                    type="number"
                    placeholder="Height"
                    value={editingMedia?.height ?? ''}
                    step="any"
                    onChange={(e) => setEditingMedia({ ...editingMedia, height: e.target.value })}
                  />
                  <Select
                    w="90px"
                    flexShrink={0}
                    value={editingMedia?.dimensionUnit || 'px'}
                    onChange={(e) => setEditingMedia({ ...editingMedia, dimensionUnit: e.target.value })}
                  >
                    <option value="px">px</option>
                    <option value="in">in</option>
                  </Select>
                </Flex>
              </FormControl>
              <FormControl>
                <FormLabel>Thumbnail</FormLabel>
                {(editingMedia?.newThumbnailPreview || editingMedia?.thumbnailPath) && (
                  <Image
                    src={editingMedia?.newThumbnailPreview || editingMedia?.thumbnailPath}
                    alt="Thumbnail"
                    maxH="120px"
                    objectFit="cover"
                    objectPosition="center top"
                    borderRadius="md"
                    mb={2}
                  />
                )}
                <Input
                  type="file"
                  accept="image/*"
                  size="sm"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setEditingMedia({
                        ...editingMedia,
                        newThumbnail: file,
                        newThumbnailPreview: URL.createObjectURL(file)
                      });
                    }
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Topics</FormLabel>
                <VStack align="flex-start" spacing={2}>
                  {topics.map((t) => (
                    <Checkbox
                      key={t.id}
                      colorScheme="blue"
                      isChecked={(editingMedia?.topicIds || []).includes(t.id)}
                      onChange={(e) => {
                        const current = editingMedia?.topicIds || [];
                        setEditingMedia({
                          ...editingMedia,
                          topicIds: e.target.checked
                            ? [...current, t.id]
                            : current.filter((id) => id !== t.id)
                        });
                      }}
                    >
                      {t.name}
                    </Checkbox>
                  ))}
                </VStack>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onMediaModalClose}>Cancel</Button>
            <Button bg="brand.ltRed" color="white" _hover={{ bg: 'brand.dkRed' }} onClick={handleUpdateMedia}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isUserModalOpen} onClose={onUserModalClose}>
        <ModalOverlay />
        <ModalContent bg="white" sx={{ 'input, select': { borderColor: 'gray.400 !important', '&:hover': { borderColor: 'gray.500 !important' }, '&:focus': { borderColor: 'brand.ltBlue !important' } } }}>
          <ModalHeader>Edit User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={editingUser?.name || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={editingUser?.email || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>New Password (leave blank to keep)</FormLabel>
                <Input
                  type="password"
                  value={editingUser?.password || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                  placeholder="Enter new password"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Role</FormLabel>
                <Select
                  value={editingUser?.role || 'viewer'}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={editingUser?.isActive ? 'active' : 'disabled'}
                  onChange={(e) => setEditingUser({ ...editingUser, isActive: e.target.value === 'active' })}
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onUserModalClose}>Cancel</Button>
            <Button bg="brand.ltRed" color="white" _hover={{ bg: 'brand.dkRed' }} onClick={handleUpdateUser}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="xl">
        <ModalOverlay />
        <ModalContent bg="white">
          <ModalCloseButton
            zIndex={10}
            bg="blackAlpha.700"
            color="white"
            borderRadius="full"
            _hover={{ bg: 'blackAlpha.900' }}
            size="md"
          />
          <ModalBody p={0}>
            {previewMedia?.fileType === 'pdf' ? (
              <iframe
                src={previewMedia?.downloadPath || previewMedia?.filePath}
                title={previewMedia?.title}
                width="100%"
                style={{ height: '70vh', border: 'none' }}
              />
            ) : previewMedia?.mediaType === 'video' ? (
              <video
                src={previewMedia?.downloadPath || previewMedia?.filePath}
                controls
                style={{ width: '100%', maxHeight: '70vh' }}
              />
            ) : (
              <Image
                src={previewMedia?.downloadPath || previewMedia?.filePath}
                alt={previewMedia?.title}
                w="100%"
                maxH="70vh"
                objectFit="contain"
              />
            )}
          </ModalBody>
          <ModalFooter>
            <VStack align="stretch" w="100%" spacing={1}>
              <Text fontWeight="600">{previewMedia?.title}</Text>
              <HStack fontSize="sm" color="brand.dkGray1" spacing={4}>
                <Text>{previewMedia?.fileType?.toUpperCase()}</Text>
                <Text>{previewMedia?.topics?.map(t => t.name).join(', ') || 'No topic'}</Text>
              </HStack>
            </VStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="white">
          <ModalHeader>{confirmAction.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{confirmAction.message}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onConfirmClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleConfirm}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDuplicateOpen} onClose={onDuplicateClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="white" maxH="85vh">
          <ModalHeader>
            <HStack spacing={2}>
              <WarningIcon color="orange.400" />
              <Text>Duplicate Files Detected</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <Text mb={4} color="brand.dkGray1">
              {duplicateItems.length} file{duplicateItems.length > 1 ? 's' : ''} already exist{duplicateItems.length === 1 ? 's' : ''} in the library.
              Uncheck any you don&apos;t want to re-upload, or change the title.
            </Text>
            <VStack spacing={4} align="stretch">
              {duplicateItems.map((item, idx) => (
                <Box
                  key={item.id}
                  border="1px solid"
                  borderColor={item.include ? 'orange.200' : 'brand.ltGray2'}
                  borderRadius="lg"
                  p={3}
                  opacity={item.include ? 1 : 0.5}
                >
                  <HStack spacing={3} align="start">
                    <Checkbox
                      isChecked={item.include}
                      colorScheme="orange"
                      mt={1}
                      onChange={(e) =>
                        setDuplicateItems((prev) =>
                          prev.map((d, i) => (i === idx ? { ...d, include: e.target.checked } : d))
                        )
                      }
                    />
                    <Box flexShrink={0} w="80px" h="60px" borderRadius="md" overflow="hidden" bg="brand.ltGray2">
                      <Image
                        src={item.existingMatch.thumbnailPath || item.existingMatch.filePath}
                        alt={item.existingMatch.title}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                        objectPosition="center top"
                      />
                    </Box>
                    <Box flex={1}>
                      <Text fontSize="sm" fontWeight="600" color="brand.dkGray2" mb={1}>
                        Existing: {item.existingMatch.title}
                      </Text>
                      <HStack fontSize="xs" color="brand.dkGray1" spacing={3} mb={2}>
                        <Text>{item.existingMatch.fileType?.toUpperCase()}</Text>
                        <Text>{formatFileSize(item.existingMatch.fileSize)}</Text>
                        {item.existingMatch.width && (
                          <Text>{(item.existingMatch.dimensionUnit || 'px') === 'in' ? `${item.existingMatch.width}" x ${item.existingMatch.height}"` : `${item.existingMatch.width} x ${item.existingMatch.height}px`}</Text>
                        )}
                      </HStack>
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0}>Upload title:</FormLabel>
                        <Input
                          size="sm"
                          value={item.customTitle}
                          onChange={(e) =>
                            setDuplicateItems((prev) =>
                              prev.map((d, i) => (i === idx ? { ...d, customTitle: e.target.value } : d))
                            )
                          }
                        />
                      </FormControl>
                    </Box>
                  </HStack>
                </Box>
              ))}
            </VStack>
            {cleanItems.length > 0 && (
              <Text mt={4} fontSize="sm" color="brand.dkGray1">
                {cleanItems.length} other file{cleanItems.length > 1 ? 's' : ''} will upload normally.
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDuplicateClose}>
              Cancel
            </Button>
            <Button
              bg="brand.ltRed"
              color="white"
              _hover={{ bg: 'brand.dkRed' }}
              onClick={handleDuplicateConfirm}
              isDisabled={!duplicateItems.some((d) => d.include) && cleanItems.length === 0}
            >
              Upload {duplicateItems.filter((d) => d.include).length + cleanItems.length} File{(duplicateItems.filter((d) => d.include).length + cleanItems.length) !== 1 ? 's' : ''}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isUnregOpen} onClose={onUnregClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaFolder} color="brand.ltRed" />
              <Text>
                {unregisteredFiles.length} Unregistered File{unregisteredFiles.length !== 1 ? 's' : ''} Detected
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="brand.dkGray1" mb={4}>
              These files were found in the uploads folder but aren't in the media library.
              Set the details below and register them.
            </Text>
            <VStack spacing={4} align="stretch">
              {unregisteredFiles.map((file) => (
                <Box
                  key={file.filename}
                  border="1px solid"
                  borderColor="brand.ltGray2"
                  borderRadius="lg"
                  p={4}
                >
                  <HStack justify="space-between" mb={3}>
                    <HStack spacing={2}>
                      <Icon
                        as={file.isVideo ? FaVideo : file.isPdf ? FaFilePdf : FaImage}
                        color={file.isVideo ? 'blue.500' : file.isPdf ? 'red.500' : 'green.500'}
                      />
                      <Text fontSize="sm" fontWeight="600" color="brand.dkGray2">
                        {file.filename}
                      </Text>
                      <Badge colorScheme="gray" fontSize="xs">{file.fileSizeFormatted}</Badge>
                      <Badge colorScheme={file.isVideo ? 'blue' : file.isPdf ? 'red' : 'green'} fontSize="xs">
                        {file.ext.toUpperCase()}
                      </Badge>
                    </HStack>
                  </HStack>
                  <SimpleGrid columns={3} spacing={3} mb={3}>
                    <FormControl>
                      <FormLabel fontSize="xs">Title</FormLabel>
                      <Input
                        size="sm"
                        value={unregEdits[file.filename]?.title || ''}
                        onChange={(e) =>
                          setUnregEdits((prev) => ({
                            ...prev,
                            [file.filename]: { ...prev[file.filename], title: e.target.value }
                          }))
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Media Type</FormLabel>
                      <Select
                        size="sm"
                        value={unregEdits[file.filename]?.mediaType || file.suggestedType}
                        onChange={(e) =>
                          setUnregEdits((prev) => ({
                            ...prev,
                            [file.filename]: { ...prev[file.filename], mediaType: e.target.value }
                          }))
                        }
                      >
                        {MEDIA_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Topics</FormLabel>
                      <Wrap spacing={1}>
                        {topics.map((t) => (
                          <WrapItem key={t.id}>
                            <Checkbox
                              size="sm"
                              colorScheme="blue"
                              isChecked={unregEdits[file.filename]?.topicIds?.includes(t.id) || false}
                              onChange={(e) => {
                                setUnregEdits((prev) => {
                                  const current = prev[file.filename]?.topicIds || [];
                                  return {
                                    ...prev,
                                    [file.filename]: {
                                      ...prev[file.filename],
                                      topicIds: e.target.checked
                                        ? [...current, t.id]
                                        : current.filter((id) => id !== t.id)
                                    }
                                  };
                                });
                              }}
                            >
                              <Text fontSize="xs">{t.name}</Text>
                            </Checkbox>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </FormControl>
                  </SimpleGrid>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      bg="brand.ltRed"
                      color="white"
                      _hover={{ bg: 'brand.dkRed' }}
                      onClick={() => handleRegisterFile(file)}
                      isLoading={registeringFile === file.filename}
                    >
                      Register
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      leftIcon={<DeleteIcon />}
                      onClick={() => handleDeleteUnregistered(file)}
                      isLoading={registeringFile === file.filename}
                    >
                      Delete
                    </Button>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onUnregClose}>
              Dismiss
            </Button>
            {unregisteredFiles.length > 1 && (
              <Button
                bg="brand.ltRed"
                color="white"
                _hover={{ bg: 'brand.dkRed' }}
                onClick={handleRegisterAll}
              >
                Register All ({unregisteredFiles.length})
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};
